import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SCREEN_WIDTH, useGlobalState } from '../../../../stateProvider/StateProvider';
import { QuestRoute } from '../../../../api/QuestRoutes';
import { exportToPDF, formatCurrency, generateId, translate } from '../../../../utility/functions';
import Page from '../../../../layout/Page';
import Button from '../../../../forms/Button';
import Row from '../../../../layout/Row';
import Card from '../../../../forms/Card';
import Col from '../../../../layout/Col';
import DateRange from '../../../../table/DateRange';
import Heading from '../../../../forms/Heading';
import Loading from '../../../../Loading';
import Select from '../../../../forms/Select';
import DailyDrop from './DailyDrop';
import Table from '../../../../table/Table';

const ReportRow = styled(Row)`
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    gap: 7em;
    border-bottom: 1px solid ${({ theme }) => theme.gray}40;
`
const Cell = styled.span`
    flex: 1;

    &:first-child {
        flex: 2;
    }
`
const Report = styled(Card)`
    width: -webkit-fill-available;
    width: -moz-available;
`
const ReportTable = styled(Table)`
    tbody tr {
        background: ${({ bColor }) => bColor}1a;
        border-width: 3px;
    }
    tbody tr:hover {
        background: ${({ bColor }) => bColor}1a;
    }
    thead th {
        background: ${({ bColor }) => bColor}40;
    }
    tbody tr:first-child {
        border-top: unset;
    }
    // thead th:first-child {
    //     border-top-left-radius: 2em;
    //     border-bottom-left-radius: 2em;
    // }
    // thead th:last-child {
    //     border-top-right-radius: 2em;
    //     border-bottom-right-radius: 2em;
    // }
`

const Daily = ({ ...props }) => {
    const {
        state: {
            active_user,
            locations,
            allLocations
        },
    } = useGlobalState();

    const cardRef = useRef();
    const [dataSet, setDataSet] = useState(null);
    const [data, setData] = useState(null);
    const [redeemed, setRedeemed] = useState(null);
    const [location, setLocation] = useState('');

    const sortIndex = [
        'Wash',
        'AppWashClubBilling',
        'PrepaidPassBilling',
        'LPR',
        'WashClubSignUp',
        'AppWashClubSignUp',
        'AppWash',
        'CodeBundle',
        'BuyUp',
        'TokenCoin',
        'ValueCardIssue',
        'Time',
        'Parking',
        'Redeemed'
    ]
    const itemColors = [
        '#4BC0C0',
        '#36A2EB',
        '#FF6384',
        '#FFCE56',
        '#9966FF',
        '#FF9F40',
        '#4BC0C0',
        '#36A2EB',
        '#FF6384',
        '#FFCE56',
        '#9966FF',
        '#FF9F40'
    ]


    const buildDataSet = (items, setItems) => {
        const dailyStats = {};

        if (location) {
            items = items.filter(e => e.locationId === location);
        }

        items.forEach(e => {
            if (dailyStats[e.itemType]) {
                const { items, locations, breakdown } = dailyStats[e.itemType];
                items.push(e);
                locations[e.location] ? locations[e.location]++ : locations[e.location] = 1;
                breakdown[e.itemId] ? breakdown[e.itemId].push(e) : breakdown[e.itemId] = [e];
            } else {
                dailyStats[e.itemType] = {
                    items: [e],
                    locations: {
                        [e.location]: 1
                    },
                    breakdown: {
                        [e.itemId]: [e]
                    }
                }
            }
        })

        Object.values(dailyStats).forEach((e) => {
            const { items } = e;

            e.totalCount = items.length;

            const nameCount = items.reduce((countMap, item) => {
                const name = item.name;
                countMap[name] = (countMap[name] || 0) + 1;
                return countMap;
            }, {});

            items.sort((a, b) => nameCount[b.name] - nameCount[a.name]);

            e.itemized = Object.entries(items.reduce((acc, transaction) => {
                const { name, price, quantity, location, discount, discountType } = transaction;

                if (!acc[name]) acc[name] = [];

                const existingItem = acc[name].find(item => item.price === price && item.discount === discount);

                if (existingItem) {
                    existingItem.quantity += quantity;
                    const existingLocation = existingItem.locations.find(loc => loc.location === location);
                    if (existingLocation) {
                        existingLocation.quantity += quantity;
                    } else {
                        existingItem.locations.push({ location, quantity });
                    }
                } else {
                    acc[name].push({ price, quantity, discount, discountType, locations: [{ location, quantity }] });
                }

                acc[name].sort((a, b) => b.quantity - a.quantity);

                return acc;
            }, {}));

            e.discountTotal = items.reduce((a, b) => a + (b.discount ?? 0), 0);
            e.subTotal = items.reduce((a, b) => a + b.price, 0);
            e.total = e.subTotal - e.discountTotal;
        })

        setItems(dailyStats);
    }

    useEffect(() => {
        if (!dataSet) return;
        dataSet.forEach(e => {
            if (e.itemType === 'BuyUp') {
                e.discount = 0;
            };
            if (e.itemType === 'WashClubReactivation') {
                e.flags = null;
            };
            if (e.flags === 'Loyalty' && !e.discountType) {
                const newName = e.name.split(',');
                e.name = newName[newName.length - 1];
                e.flags = null;
            };
        })
        buildDataSet(dataSet.filter(e => !e.flags), setData)
        buildDataSet(dataSet.filter(e => e.flags), setRedeemed)
    }, [dataSet, location])

    useEffect(() => {
        if (!data) return;
    }, [data])

    return (
        <Page>

            <Row wrap>
                <Select
                    label='Location'
                    options={[{ value: '', label: 'All Locations' }, ...allLocations]}
                    value={location}
                    onChange={(e) => setLocation(parseInt(e.target.value))}
                    standalone
                    style={{ minWidth: '10em' }}
                />
                <DateRange
                    route={QuestRoute.reports.daily}
                    setData={setDataSet}
                    searchLimit={300}
                    monthStart={false}
                />
            </Row>

            <Button
                label='Export'
                disabled={!data}
                onClick={() => exportToPDF(cardRef, 'Sales Summary')}
            />

            <Report title='Sales Summary' icon='clock'>
                <div ref={cardRef}>
                    {data ?
                        Object.entries(data).length ?
                            <Col style={{ gap: '5em' }}>
                                <Col>
                                    <Heading>Sales</Heading>
                                    <ReportRow style={{ background: '#6fcc6c40', padding: '0.3em 0' }}>
                                        {SCREEN_WIDTH ?
                                            <Cell>
                                                <b style={{ padding: '0 0.3em' }}>GROSS REVENUE: {formatCurrency(Object.values(data).reduce((a, b) => a + b.total, 0))}</b>
                                            </Cell> :
                                            <>
                                                <Cell>
                                                    <b style={{ padding: '0 0.3em' }}>GROSS REVENUE</b>
                                                </Cell>
                                                <Cell></Cell>
                                                <Cell>
                                                    <b>{formatCurrency(Object.values(data).reduce((a, b) => a + b.total, 0))}</b>
                                                </Cell>
                                            </>
                                        }
                                    </ReportRow>

                                    {Object.entries(data)
                                        .sort((a, b) => sortIndex.indexOf(a[0]) - sortIndex.indexOf(b[0]))
                                        .map(([key, val], i) => {
                                            const heading = key;
                                            const { itemized, total, totalCount } = val;

                                            return (
                                                <ReportTable
                                                    key={`salesTable-${i}`}
                                                    bColor={itemColors[i]}
                                                    config={{
                                                        colSpacing: [30, 20, 30, 20],
                                                        disableTableControls: true,
                                                        disableHeaderSearch: true,
                                                        disableAnimation: true,
                                                        data: itemized,
                                                        headers: [{
                                                            label: translate(heading),
                                                        }, {
                                                            label: 'Quantity',
                                                        }, {
                                                            label: 'Price',
                                                        }, {
                                                            label: 'Sales: ' + formatCurrency(total),
                                                        }],
                                                        renderFormat: (e) => (
                                                            <tr key={generateId()}>
                                                                <DailyDrop
                                                                    label={e[0]}
                                                                    quantity={e[1].map(i => i.quantity)}
                                                                    price={e[1].map(i => formatCurrency(Math.max(0, i.price - (i.discount ?? 0))))}
                                                                    discount={e[1].map(i => i.discount)}
                                                                    discountType={e[1].map(i => i.discountType)}
                                                                    locations={e[1].map(i => i.locations)}
                                                                />
                                                                <td>{formatCurrency(e[1].reduce((a, b) => a + (b.price - (b.discount ?? 0)) * b.quantity, 0))}</td>
                                                            </tr>
                                                        )
                                                    }}
                                                />
                                            )
                                        })}
                                </Col>

                                {Object.entries(redeemed).length ?
                                    <Col>
                                        <Heading>Redemptions</Heading>
                                        {Object.entries(redeemed).map(([key, val], i) => {
                                            const heading = key;
                                            const { itemized, total, totalCount } = val;

                                            return (
                                                <ReportTable
                                                    key={`reportTable-${i}`}
                                                    bColor={itemColors[i]}
                                                    config={{
                                                        colSpacing: [30, 20, 30, 20],
                                                        disableTableControls: true,
                                                        disableHeaderSearch: true,
                                                        disableAnimation: true,
                                                        data: itemized,
                                                        headers: [{
                                                            label: translate(heading),
                                                        }, {
                                                            label: 'Quantity',
                                                        }],
                                                        renderFormat: (e) => (
                                                            <tr key={generateId()}>
                                                                <DailyDrop
                                                                    label={e[0]}
                                                                    quantity={e[1].map(i => i.quantity)}
                                                                    discount={e[1].map(i => i.discount)}
                                                                    discountType={e[1].map(i => i.discountType)}
                                                                    locations={e[1].map(i => i.locations)}
                                                                />
                                                            </tr>
                                                        )
                                                    }}
                                                />
                                            )
                                        })}
                                        {/* <ReportRow>
                                            <Cell>
                                                <b>GROSS REDEEMED</b>
                                            </Cell>
                                            <Cell></Cell>
                                            <Cell style={{ textAlign: 'right' }}>
                                                <b>{formatCurrency(Object.values(redeemed).reduce((a, b) => a + b.total, 0))}</b>
                                            </Cell>
                                        </ReportRow> */}
                                    </Col> : null}

                            </Col>
                            : <>Nothing to Report</>
                        : <Loading />
                    }
                </div>
            </Report>
        </Page >
    );
}

export default Daily;